<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="loading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" clearable hide-details />
            </template>

            <template #default>
              <v-row>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.entity_type_id"
                    :disabled="loading"
                    :items="entityTypes"
                    label="Entity Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-col sm="4" v-on="isSet(query.params.is_default) ? on : null">
                      <select-list
                        v-model="query.params.entity_id"
                        :disabled="loading || isSet(query.params.is_default)"
                        :items="entities"
                        label="Entity"
                        clearable
                        hide-details
                      />
                    </v-col>
                  </template>
                  <span>Clear "Default Only" filter to use Entity filter</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-col sm="4" v-on="isSet(query.params.entity_id) ? on : null">
                      <select-list
                        v-model="query.params.is_default"
                        :items="[
                          { key: 'Yes', value: true },
                          { key: 'No', value: false },
                        ]"
                        :disabled="loading || isSet(query.params.entity_id)"
                        hint=""
                        label="Default Buy Guides Only"
                        clearable
                        hide-details
                      />
                    </v-col>
                  </template>
                  <span>Clear Entity filter to use "Default Only" filter</span>
                </v-tooltip>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.type"
                    :disabled="loading"
                    :items="getItemsForOption('buyGuideTypes')"
                    label="Guide Type"
                    custom-type="icon"
                    clearable
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>

            <template #content>
              <data-table
                :footer-props="{ 'items-per-page-options': [20, 40, 60, 100] }"
                :headers="headers"
                :items="buyGuides"
                :items-per-page="pagination.perPage"
                :page="pagination.currentPage"
                :route="{ name: 'content.buy-guides.edit', params: ['id'] }"
                :server-items-length="pagination.total"
                disable-sort
                @update:items-per-page="$_queryHandler_onUpdateItemsPerPage"
                @update:page="$_queryHandler_onUpdatePage"
              >
                <template #item.entity="{ item }">
                  <template v-if="item.entity_id">
                    <item-logo :name="getEntityName(item)" :src="getEntityLogo(item)" class="ml-0 mr-2" size="30" />
                    <span>{{ getEntityName(item) }}</span>
                  </template>
                  <base-chip v-else text="Default" small />
                </template>
                <template #item.entityType="{ item }">
                  {{ getEntityTypeName(item) || '-' }}
                </template>
                <template #item.type="{ value }">
                  <v-icon small>{{ getTypeIcon(value) }}</v-icon>
                  {{ formattedOption('buyGuideTypes', value) }}
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>
    <buy-guide-create-modal
      v-model="modalVisible"
      :types="getItemsForOption('buyGuideTypes')"
      :entities="entities"
      :entity-types="entityTypes"
      :languages="languages"
      @toggle="toggleModal"
    />
    <fab-create v-if="isPermittedTo('create')" type="buy guide" @click="toggleModal" />
  </page-loader>
</template>

<script>
// Helpers
import { mapActions, mapGetters, mapMutations } from 'vuex'
import isSet from '@/utils/is-set'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Components
import BaseChip from '@/components/chips/base-chip'
import BuyGuideCreateModal from './components/buy-guide-create-modal.vue'
import DataTable from '@/components/data-table'
import FabCreate from '@/components/fab-create'
import FilterAccordion from '@/components/filter-accordion'
import ItemLogo from '@/components/item-logo'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
// Services
import { buyGuides, entities, languages } from '@/services/offers'

export default {
  name: 'BuyGuidesIndex',
  components: {
    BaseChip,
    BuyGuideCreateModal,
    DataTable,
    FabCreate,
    FilterAccordion,
    ItemLogo,
    PageLoader,
    SearchInput,
    SelectList,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data() {
    return {
      buyGuides: [],
      entities: [],
      headers: [
        { text: 'Entity', value: 'entity' },
        { text: 'Entity Type', value: 'entityType' },
        { text: 'Type', value: 'type' },
      ],
      languages: [],
      loading: false,
      modalVisible: false,
      pagination: {
        perPage: 20,
      },
      queriesDefined: [
        'type',
        'language_id',
        'entity_id',
        'entity_type_id',
        'is_default',
        'searchTerm',
        'perPage',
        'currentPage',
      ],
      queryTypes: { entity_type_id: 'number', entity_id: 'number' },
    }
  },
  computed: {
    ...mapGetters({
      entityTypes: 'entityTypes/getEntityTypeOptions',
      getItemsForOption: 'options/getItemsForOption',
      formattedOption: 'options/formattedOption',
      isPageLoading: 'general/isPageLoading',
    }),
  },
  watch: {
    'query.params.is_default'(value) {
      if (value !== undefined) this.$set(this.query, 'params', { ...this.query.params, entity_id: undefined })
    },
    'query.params.entity_id'(value) {
      if (value) this.$set(this.query, 'params', { ...this.query.params, is_default: undefined })
    },
  },
  async created() {
    const paginate = { perPage: null }
    await Promise.all([this.fetchEntityTypes({ paginate }), this.fetchEntities(paginate), this.fetchLanguages()])
    await this.fetchBuyGuides()
    this.setPageLoading(false)
  },
  methods: {
    ...mapActions({
      fetchEntityTypes: 'entityTypes/fetch',
    }),
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    isSet,
    async fetchBuyGuides() {
      this.loading = true
      try {
        const { perPage, currentPage: page } = this.pagination
        const language_id = this.languages.find(({ key }) => key === 'English').value
        const {
          data,
          pagination: { total },
        } = await buyGuides.getAll({
          search: this.query.searchTerm,
          paginate: { perPage, page },
          filters: { ...this.query.params, language_id },
        })

        this.buyGuides = data
        this.pagination.total = total
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to retrieve Buy Guides' })
      }
      this.loading = false
    },
    async fetchEntities(paginate) {
      try {
        this.entities = await entities.getEntities(paginate, true)
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to fetch entities' })
      }
    },
    async fetchLanguages() {
      try {
        this.languages = (await languages.getLanguages()).map(({ name, id }) => ({ key: name, value: id }))
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to fetch languages' })
      }
    },
    getEntityLogo(guide) {
      return guide?.entity?.entity?.default?.logoFeatured
    },
    getEntityName(guide) {
      return guide?.entity?.entity?.default?.name
    },
    getEntityTypeName(guide) {
      const entityTypeId = guide?.entity?.entity_type_id || guide.entityType?.id || guide.entity_type_id
      return this.entityTypes.find(({ value }) => value === entityTypeId)?.key
    },
    getTypeIcon(type) {
      return this.getItemsForOption('buyGuideTypes').find(({ value }) => value === type)?.icon
    },
    async handleQueryUpdate() {
      if (this.isPageLoading) return
      await this.fetchBuyGuides({ perPage: this.pagination.perPage, currentPage: 1 })
    },
    toggleModal() {
      this.modalVisible = !this.modalVisible
    },
  },
}
</script>
